import {
  Box,
  Button,
  Grid,
  Hidden,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import React, { useEffect, useMemo, useState } from 'react';
import NoItemBox from '../../../shared-components/NoItemBox';
import { ICategory } from '../../../shared-interfaces/Notation/ICategory';
import CategoryService from './Services/CategoryService';
import AddNewCategoryDialog from './Components/AddNewCategoryDialog';
import CategoryListItem, {
  CategoryListItemLoader,
} from './Components/CategoryListItem';

const useStyles = makeStyles({
  listItem: {
    borderBottom: '1px solid silver',
  },
  listHeading: {
    fontWeight: 600,
  },
  subtitle2: {
    fontStyle: 'italic',
    color: '#666',
  },
  content: {
    backgroundColor: 'white',
    padding: '30px',
    boxShadow: '2px 2px 0 rgba(0,0,0,.1)',
    borderLeft: '1px solid #e0e0e0',
    borderTop: '1px solid #e0e0e0',
  },
});

enum Filter {
  All,
  Active,
  Inactive,
}

const NotationCategories = () => {
  const classes = useStyles();
  const MAX_ACTIVE_CATEGORIES = 30;

  const [isLoading, setIsLoading] = useState(true);
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<ICategory[]>([]);
  const [filter, setFilter] = useState(Filter.All);
  const [open, setOpen] = useState(false);

  const [filterMenuAnchorEl, setFilterMenuAnchorEl] = React.useState<
    (EventTarget & HTMLButtonElement) | null
  >(null);

  const enabledCategoryCount = useMemo(() => {
    return categories.filter(c => c.isActive).length;
  }, [categories]);

  useEffect(() => {
    CategoryService.getCategories(false)
      .then(c => {
        setCategories(c);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    switch (filter) {
      case Filter.All: {
        setFilteredCategories(
          categories.sort((a, b) => {
            return a.name.toUpperCase() > b.name.toUpperCase() ? 1 : -1;
          }),
        );
        break;
      }
      case Filter.Active: {
        setFilteredCategories(
          categories
            .filter(c => {
              return c.isActive;
            })
            .sort((a, b) => {
              return a.name.toUpperCase() > b.name.toUpperCase() ? 1 : -1;
            }),
        );
        break;
      }
      case Filter.Inactive: {
        setFilteredCategories(
          categories
            .filter(c => {
              return !c.isActive;
            })
            .sort((a, b) => {
              return a.name.toUpperCase() > b.name.toUpperCase() ? 1 : -1;
            }),
        );
        break;
      }
    }
  }, [filter, categories, categories.length]);

  const onCreate = (newCategory: ICategory) => {
    setCategories([...categories, newCategory]);
  };

  return <>
    <Box display="flex">
      <Box flexGrow={1}>
        <Typography variant="h1">Notation Categories</Typography>
      </Box>
      <Box>
        <Button
          color="primary"
          variant="contained"
          startIcon={<AddIcon />}
          style={{ marginTop: '10px' }}
          onClick={() => {
            setOpen(true);
          }}
        >
          <Hidden smDown>Add New Category</Hidden>
          <Hidden smUp>New</Hidden>
        </Button>
      </Box>
    </Box>
    <Paper className={classes.content}>
      <Box
        display="flex"
        style={{ width: '100%', paddingLeft: 0, paddingRight: '4px' }}
      >
        <Box flexGrow={1}>
          <Typography variant="subtitle2" className={classes.subtitle2}>
            Authors of updates and alerts can select active categories to
            describe their content.
          </Typography>
          <Typography variant="subtitle2" className={classes.subtitle2}>
            {`Only up to ${MAX_ACTIVE_CATEGORIES} categories can be active at once.`}
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            aria-haspopup="true"
            onClick={e => {
              setFilterMenuAnchorEl(e.currentTarget);
            }}
            startIcon={
              <Hidden lgDown>
                <FilterListIcon />
              </Hidden>
            }
          >
            <Hidden lgUp>
              <FilterListIcon />
            </Hidden>
            <Hidden mdDown>
              {filter === Filter.All
                ? 'All Categories'
                : filter === Filter.Active
                ? 'Active Categories'
                : filter === Filter.Inactive
                ? 'Inactive Categories'
                : '???'}
            </Hidden>
          </Button>
          <Menu
            anchorEl={filterMenuAnchorEl}
            open={Boolean(filterMenuAnchorEl)}
            onClose={() => setFilterMenuAnchorEl(null)}
          >
            <MenuItem
              onClick={() => {
                setFilter(Filter.All);
                setFilterMenuAnchorEl(null);
              }}
            >
              All Categories
            </MenuItem>
            <MenuItem
              onClick={() => {
                setFilter(Filter.Active);
                setFilterMenuAnchorEl(null);
              }}
            >
              Active Categories
            </MenuItem>
            <MenuItem
              onClick={() => {
                setFilter(Filter.Inactive);
                setFilterMenuAnchorEl(null);
              }}
            >
              Inactive Categories
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      <Grid container spacing={4}>
        <Grid item sm={12} md={12} lg={12}>
          <List>
            <ListItem className={classes.listItem}>
              <ListItemText
                primary="Name"
                className={classes.listHeading}
                disableTypography
              />
              <ListItemSecondaryAction className={classes.listHeading}>
                {`Active (${enabledCategoryCount}/${MAX_ACTIVE_CATEGORIES})`}
              </ListItemSecondaryAction>
            </ListItem>
            {isLoading ? (
              [...Array(30)].map((x, i) => {
                return <CategoryListItemLoader key={i} />;
              })
            ) : filteredCategories.length === 0 ? (
              <NoItemBox
                height={575}
                minHeight={497}
                text="No Categories To Display"
              />
            ) : (
              filteredCategories.map(x => {
                return (
                  <CategoryListItem
                    key={x.id}
                    category={x}
                    canEnable={enabledCategoryCount < MAX_ACTIVE_CATEGORIES}
                    onUpdate={c => {
                      var index = categories.findIndex(c2 => c2.id === c.id);
                      setCategories(cats => {
                        cats.splice(index, 1, c);
                        return [...cats];
                      });
                    }}
                  />
                );
              })
            )}
          </List>
        </Grid>
      </Grid>
    </Paper>
    <AddNewCategoryDialog
      open={open}
      onCreate={onCreate}
      onClose={() => setOpen(false)}
      canEnable={enabledCategoryCount < MAX_ACTIVE_CATEGORIES}
    />
  </>;
};

export default NotationCategories;
